// Fog volumes like ET's renderer: every opaque surface inside a fog brush (the
// BSP surface's fogNum) gets an extra pass blending the fog colour over it
// (RB_FogPass, depth equal), with the density read from the 16x16 fog image of
// R_CreateFogImage at texture coordinates from RB_CalcFogTexCoords:
//   s = distance along the view direction / depthForOpaque
//   t = depth below the fog's visible side / depthForOpaque (+ the eye's depth
//       when the eye is inside), or 1 for fogs without a visible side.
// A global fog (brush -1, or the worldspawn "_fog" shader) fogs every surface
// that is in no other fog and colours the background.

import * as THREE from 'three';
import { FOG_S, FOG_T, fogImageData, type FogVolume } from './fogVolumes';
import type { SharedUniforms } from './q3shader';

export { fogImageData, fogTexCoords, loadFogs, type FogVolume, type MapFogs } from './fogVolumes';

export function createFogImage(): THREE.DataTexture {
  const tex = new THREE.DataTexture(fogImageData(), FOG_S, FOG_T, THREE.RGBAFormat);
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearFilter;
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.flipY = false;
  tex.needsUpdate = true;
  return tex;
}

const FOG_VERTEX = /* glsl */ `precision highp float;
uniform mat4 modelMatrix;
uniform mat4 viewMatrix;
uniform mat4 projectionMatrix;
uniform vec3 cameraPosition;
uniform vec4 uFogDepth;
uniform float uTcScale;
uniform bool uHasSurface;
in vec3 position;
out vec2 vFog;
void main() {
  vec4 world = modelMatrix * vec4(position, 1.0);
  vec4 view = viewMatrix * world;
  float s = -view.z * uTcScale;
  float t = 1.0;
  if (uHasSurface) {
    t = dot(world.xyz, uFogDepth.xyz) + uFogDepth.w;
    float eyeT = dot(cameraPosition, uFogDepth.xyz) + uFogDepth.w;
    if (eyeT >= 0.0) t += eyeT;
  }
  vFog = vec2(s, t);
  // the same expression as the surface shaders: the depth must match exactly (depth func equal)
  gl_Position = projectionMatrix * viewMatrix * world;
}`;

const FOG_FRAGMENT = /* glsl */ `precision highp float;
uniform sampler2D uFogImage;
uniform vec3 uFogColor;
uniform float uBrightness;
in vec2 vFog;
out vec4 fragColor;
void main() {
  fragColor = vec4(min(uFogColor * uBrightness, vec3(1.0)), texture(uFogImage, vFog).a);
}`;

/** The fog pass of the surfaces of one fog volume. */
export function createFogMaterial(fog: FogVolume, image: THREE.Texture, shared: SharedUniforms): THREE.RawShaderMaterial {
  const s = fog.surface;
  const mat = new THREE.RawShaderMaterial({
    glslVersion: THREE.GLSL3,
    vertexShader: FOG_VERTEX,
    fragmentShader: FOG_FRAGMENT,
    uniforms: {
      uFogImage: { value: image },
      uFogColor: { value: new THREE.Vector3(...fog.color) },
      uBrightness: shared.uBrightness,
      uTcScale: { value: fog.tcScale },
      uHasSurface: { value: !!s },
      uFogDepth: { value: s ? new THREE.Vector4(s[0] * fog.tcScale, s[1] * fog.tcScale, s[2] * fog.tcScale, -s[3] * fog.tcScale) : new THREE.Vector4() },
    },
    transparent: true,
    depthWrite: false,
    depthFunc: THREE.EqualDepth,
    side: THREE.DoubleSide,
  });
  mat.blending = THREE.CustomBlending;
  mat.blendSrc = THREE.SrcAlphaFactor;
  mat.blendDst = THREE.OneMinusSrcAlphaFactor;
  mat.blendSrcAlpha = THREE.ZeroFactor;
  mat.blendDstAlpha = THREE.OneFactor;
  return mat;
}
